import { useMemo, useState } from "react";
import { X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Field, Input, Label } from "@/components/ui/field";
import { Alert } from "@/components/ui/alert";
import { MediaPicker } from "@/components/media-picker";
import { GroupPicker } from "@/components/group-picker";
import { MessageVariables } from "@/components/message-variables";
import { cn } from "@/lib/cn";
import { useGroupSets } from "@/lib/groups";
import { useCreateCampaign, useUpdateCampaign, type Campaign } from "@/lib/campaigns";

export function CampaignEditor({
    accountId,
    editing,
    onClose,
}: {
    accountId: string;
    editing: Campaign | null;
    onClose: () => void;
}) {
    const [name, setName] = useState(editing?.name ?? "");
    const [text, setText] = useState(editing?.messageText ?? "");
    const [groupSetIds, setGroupSetIds] = useState<string[]>(editing?.groupSetIds ?? []);
    const [groupIds, setGroupIds] = useState<string[]>(editing?.groupIds ?? []);
    const [mediaIds, setMediaIds] = useState<string[]>(editing?.media.map((item) => item.id) ?? []);

    const { data: groupSets } = useGroupSets(accountId);
    const create = useCreateCampaign();
    const update = useUpdateCampaign();

    // Số nhóm thực nhận: gộp group set với nhóm lẻ rồi khử trùng, đúng như
    // dispatch làm ở server.
    const targetCount = useMemo(() => {
        const ids = new Set(groupIds);
        for (const set of groupSets ?? []) {
            if (groupSetIds.includes(set.id)) for (const id of set.groupIds) ids.add(id);
        }
        return ids.size;
    }, [groupIds, groupSetIds, groupSets]);

    const pending = create.isPending || update.isPending;
    const error = create.error ?? update.error;
    const canSave = name.trim() && (text.trim() || mediaIds.length > 0) && (groupSetIds.length > 0 || groupIds.length > 0);

    function handleSave() {
        const payload = {
            accountId,
            name,
            messageText: text,
            mode: "SEQUENTIAL" as const,
            groupSetIds,
            groupIds,
            mediaIds,
        };

        if (editing) update.mutate({ id: editing.id, ...payload }, { onSuccess: onClose });
        else create.mutate(payload, { onSuccess: onClose });
    }

    return (
        <div className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-ink/40 p-4">
            <div role="dialog" aria-modal="true" className="my-8 w-full max-w-2xl rounded-xl bg-surface shadow-xl">
                <div className="flex items-center justify-between border-b border-line px-5 py-4">
                    <h2 className="text-base font-semibold text-ink">
                        {editing ? "Sửa chiến dịch" : "Chiến dịch mới"}
                    </h2>
                    <button onClick={onClose} aria-label="Đóng" className="text-ink-muted hover:text-ink">
                        <X className="size-4" />
                    </button>
                </div>

                <div className="space-y-5 px-5 py-5">
                    {error && <Alert tone="danger">{error.message}</Alert>}

                    <Field label="Tên chiến dịch" htmlFor="campaign-name">
                        <Input
                            id="campaign-name"
                            placeholder="VD: Thông báo bảng giá tháng 8"
                            value={name}
                            onChange={(event) => setName(event.target.value)}
                        />
                    </Field>

                    <div className="space-y-1.5">
                        <Label htmlFor="campaign-text">Nội dung</Label>
                        <textarea
                            id="campaign-text"
                            rows={5}
                            value={text}
                            onChange={(event) => setText(event.target.value)}
                            placeholder={"Nội dung gửi tới các nhóm…\nDùng {{ten_nhom}} để mỗi nhóm nhận một tin riêng."}
                            className="w-full rounded-lg border border-line bg-surface px-3 py-2 text-sm text-ink placeholder:text-ink-muted/60 focus:border-brand focus:outline-none focus:ring-2 focus:ring-brand/20"
                        />
                        <MessageVariables
                            accountId={accountId}
                            text={text}
                            onInsert={(token) => setText((prev) => `${prev}${token}`)}
                        />
                    </div>

                    <div className="space-y-1.5">
                        <Label>Ảnh / video đính kèm</Label>
                        <MediaPicker selected={mediaIds} onChange={setMediaIds} />
                    </div>

                    <div className="space-y-2">
                        <Label>
                            Nhóm gửi
                            {targetCount > 0 && (
                                <span className="ml-1.5 font-normal text-ink-muted">
                                    — {targetCount} nhóm sẽ nhận tin
                                </span>
                            )}
                        </Label>
                        {groupSets?.length === 0 && (
                            <p className="text-xs text-ink-muted">
                                Chưa có nhóm gửi nào — chọn trực tiếp từ danh sách nhóm bên dưới.
                            </p>
                        )}
                        <div className="flex flex-wrap gap-2">
                            {groupSets?.map((set) => {
                                const active = groupSetIds.includes(set.id);
                                return (
                                    <button
                                        key={set.id}
                                        type="button"
                                        onClick={() =>
                                            setGroupSetIds(
                                                active
                                                    ? groupSetIds.filter((id) => id !== set.id)
                                                    : [...groupSetIds, set.id],
                                            )
                                        }
                                        className={cn(
                                            "rounded-lg border px-3 py-1.5 text-sm transition",
                                            active
                                                ? "border-brand bg-brand-soft font-medium text-brand"
                                                : "border-line text-ink-muted hover:text-ink",
                                        )}
                                    >
                                        {set.name} ({set.sendableCount})
                                    </button>
                                );
                            })}
                        </div>
                    </div>

                    <div className="space-y-1.5">
                        <Label>Chọn thêm nhóm lẻ</Label>
                        <GroupPicker
                            accountId={accountId} selected={groupIds} onChange={setGroupIds} />
                    </div>
                </div>

                <div className="flex justify-end gap-2 border-t border-line px-5 py-4">
                    <Button variant="ghost" onClick={onClose}>
                        Huỷ
                    </Button>
                    <Button loading={pending} disabled={!canSave} onClick={handleSave}>
                        {editing ? "Lưu thay đổi" : "Tạo chiến dịch"}
                    </Button>
                </div>
            </div>
        </div>
    );
}
